import { Grid, Typography } from "@mui/material";
import { DateTimeInput } from "components/FormControls/DateTimeInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TimeInput } from "components/FormControls/TimeInput";
import { FormikErrors } from "formik";
import { useMessage } from "hooks/intl";
import { capitalize } from "lodash";
import {
  AchievementActivity,
  AchievementFEFormat,
  availableAchievementActivityCondition,
} from "types/achievements";
import { dayNames, monthNames } from "utilities/dateFormat";
import { useStyles } from "../../AchievementForm.styles";

interface Props {
  activity: AchievementActivity;
  errors?: any;
  setFieldValue: (field: string, value: any) => void;
  setErrors: (errors: FormikErrors<AchievementFEFormat>) => void;
}

export function TimeframeSection({
  activity,
  errors,
  setFieldValue,
  setErrors,
}: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const { operator } = activity;

  const activityErrors = errors && errors?.activity?.data;

  return (
    <>
      <Typography mt="16px" fontWeight={500}>
        {message({ id: "achievementAvailability" })}
      </Typography>
      <Typography className={classes.descriptionNote}>
        {message({ id: "achievementAvailabilityInfo" })}
      </Typography>
      <Grid container spacing={10}>
        <Grid xs={12} item>
          <SelectInput
            width="100%"
            data-test-id="timeframe-condition"
            items={availableAchievementActivityCondition.map((option) => ({
              id: option,
              label: message({ id: option }),
            }))}
            label={<RequiredFieldLabel text={message({ id: "timeframe" })} />}
            name="activity.operator"
            onChange={(event) => {
              setErrors({ ...errors, activity: undefined });
              return [
                "is_between",
                "is_not_between",
                "is_time_between",
              ].includes(event.target.value as string)
                ? setFieldValue("activity.data", { from: null, to: null })
                : event.target.value === "always_available"
                  ? setFieldValue("activity", { operator: "always_available" })
                  : setFieldValue("activity.data", null);
            }}
          />
        </Grid>
        {operator && (
          <>
            {["is_after", "is_before"].includes(operator) && (
              <Grid xs={12} item>
                <DateTimeInput
                  width="100%"
                  data-test-id="timeframe"
                  format="yyyy-MM-dd HH:mmXXX"
                  label={<RequiredFieldLabel text={message({ id: "value" })} />}
                  name="activity.data"
                  error={activityErrors}
                />
              </Grid>
            )}
            {["is_between", "is_not_between"].includes(operator) && (
              <>
                <Grid xs={6} item>
                  <DateTimeInput
                    width="100%"
                    data-test-id="timeframe-from"
                    format="yyyy-MM-dd HH:mmXXX"
                    label={
                      <RequiredFieldLabel text={message({ id: "valueFrom" })} />
                    }
                    name="activity.data.from"
                    error={activityErrors?.from}
                  />
                </Grid>
                <Grid xs={6} item>
                  <DateTimeInput
                    width="100%"
                    data-test-id="timeframe-to"
                    format="yyyy-MM-dd HH:mmXXX"
                    label={
                      <RequiredFieldLabel text={message({ id: "valueTo" })} />
                    }
                    name="activity.data.to"
                    error={activityErrors?.to}
                  />
                </Grid>
              </>
            )}
            {operator === "is_time_between" && (
              <>
                <Grid xs={6} item>
                  <TimeInput
                    fullWidth
                    data-test-id="timeframe-from"
                    label={
                      <RequiredFieldLabel text={message({ id: "valueFrom" })} />
                    }
                    name="activity.data.from"
                    error={activityErrors?.from}
                  />
                </Grid>
                <Grid xs={6} item>
                  <TimeInput
                    fullWidth
                    data-test-id="timeframe-to"
                    label={
                      <RequiredFieldLabel text={message({ id: "valueTo" })} />
                    }
                    name="activity.data.to"
                    error={activityErrors?.to}
                  />
                </Grid>
              </>
            )}
            {operator === "is_day_of_week" && (
              <Grid xs={12} item>
                <SelectInput
                  multiple
                  name="activity.data"
                  items={dayNames.map((day: string) => ({
                    label: capitalize(message({ id: day })),
                    id: day,
                  }))}
                  label={<RequiredFieldLabel text={message({ id: "value" })} />}
                  placeholder={message({ id: "day" })}
                  data-test-id="timeframe-value-input"
                  error={activityErrors}
                />
              </Grid>
            )}
            {operator === "is_month_of_year" && (
              <Grid xs={12} item>
                <SelectInput
                  multiple
                  name="activity.data"
                  items={monthNames.map((month: string) => ({
                    label: capitalize(message({ id: month })),
                    id: month,
                  }))}
                  label={<RequiredFieldLabel text={message({ id: "value" })} />}
                  placeholder={message({ id: "month" })}
                  data-test-id="timeframe-value-input"
                  error={activityErrors}
                />
              </Grid>
            )}
            {operator === "is_day_of_month" && (
              <Grid xs={12} item>
                <SelectInput
                  className={classes.fullWidth}
                  data-test-id="timeframe-value-input"
                  items={[...Array(31)].map((_, i) => {
                    return { id: `${i + 1}`, label: `${i + 1}` };
                  })}
                  label={<RequiredFieldLabel text={message({ id: "value" })} />}
                  name="activity.data"
                  error={activityErrors}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </>
  );
}
